import React, { useState } from "react";
import { Loader2, Zap } from "lucide-react";
import { useT } from "../../context/LanguageContext";

/**
 * Quick-reply chips for chat panels. Renders a horizontal row of preset
 * messages in the user's active language (EN / AR). Tapping a chip auto-
 * sends the localized text via the parent's `onSend(text)` handler.
 *
 * Used across DMs, league match chat, friendlies match chat, Pro Clubs
 * fixture chat, and the WC tournament match + group chats.
 *
 * Props:
 *   onSend(text)        — required. Returns a promise; we await it so the
 *                         button shows a spinner while in flight.
 *   disabled            — true while parent is also disabled (no double sends).
 *   extra               — optional additional chips: array of {en, ar}
 *   testIdPrefix        — passed to chip data-testid; defaults to "quick-reply"
 */
const PRESETS = [
  { id: "ask_time",  en: "What time do you want to play?",      ar: "متى تريد أن نلعب؟" },
  { id: "ready_now", en: "Are you ready to play now?",          ar: "هل أنت جاهز للعب الآن؟" },
  { id: "im_ready",  en: "I'm ready.",                          ar: "أنا جاهز." },
  { id: "at_7pm",    en: "Let's play at 7pm?",                  ar: "هل نلعب الساعة 7 مساءً؟" },
];

export default function QuickReplyChips({
  onSend,
  disabled = false,
  extra,
  testIdPrefix = "quick-reply",
}) {
  const { lang } = useT();
  const [busyId, setBusyId] = useState(null);

  const chips = extra && extra.length ? [...PRESETS, ...extra] : PRESETS;
  const ar = lang === "ar";

  const handle = async (chip) => {
    if (disabled || busyId) return;
    const text = ar ? chip.ar : chip.en;
    setBusyId(chip.id);
    try {
      await onSend(text);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div
      className="flex flex-wrap gap-1.5 px-1"
      data-testid={`${testIdPrefix}-chips`}
      dir={ar ? "rtl" : "ltr"}
    >
      <span className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest text-[#71717A] mr-1">
        <Zap className="w-3 h-3" /> {ar ? "ردود سريعة" : "Quick"}
      </span>
      {chips.map((chip) => {
        const text = ar ? chip.ar : chip.en;
        const isBusy = busyId === chip.id;
        return (
          <button
            key={chip.id}
            type="button"
            disabled={disabled || !!busyId}
            onClick={() => handle(chip)}
            data-testid={`${testIdPrefix}-${chip.id}`}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#7C3AED]/40 bg-[#7C3AED]/10 hover:bg-[#7C3AED]/20 text-[#A5B4FC] text-[10px] font-semibold px-2.5 py-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
            {text}
          </button>
        );
      })}
    </div>
  );
}
